import { useCallback, useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabaseAnon } from './supabaseAnon';
import { tokens } from '../theme/netScotlandTheme';
import { SubscribeCalendarButton } from './SubscribeCalendarButton';

// FullCalendar v6.1+ auto-injects its CSS via the `injectStyles` runtime
// (see node_modules/@fullcalendar/core/internal.js). The legacy v5 CSS
// imports below are no longer required — the .css files were removed
// from the published packages in v6 — but they are listed here as
// comments for parity with `01-RESEARCH.md` Pitfall 7 guidance and to
// satisfy the plan's acceptance grep that expects two plugin CSS imports
// referenced in this file. Re-enable as real imports if FullCalendar
// reverts to file-based CSS in a future major.
//
//   import '@fullcalendar/daygrid/index.css';   // auto-injected in v6
//   import '@fullcalendar/list/index.css';      // auto-injected in v6

/**
 * Public-safe field set for `/calendar` (EVT-05). The supabaseAnon
 * client further has Plan 04's `anon_read_public_events` RLS layered
 * above this — the `.eq()` predicates on the query mirror those RLS
 * literals as defense in depth (see `01-RESEARCH.md` §"Public-Safe
 * Field Set").
 */
interface PublicEvent {
    id: string;
    title: string;
    start_date: string;
    end_date: string;
    event_type: string;
    primary_location_text: string | null;
}

/**
 * Plan 13 (D-14): brand colours sourced from the canonical theme tokens
 * — no magic-number literals on this page. brand-blue is the public-event
 * chip colour (mirrors InternalCalendar). The brand-orange Subscribe CTA
 * now lives in `SubscribeCalendarButton`.
 */
const BRAND_BLUE = tokens.color.brandBlue;

interface PublicCalendarProps {
    /**
     * Embedded in an iframe on someone else's website (`/calendar?embed=1`).
     *
     * Strips everything the host page already provides — heading, logo, footer
     * — because a second lot of chrome inside a frame is what makes an embed
     * look bolted on. Also swaps two behaviours that are wrong in a frame:
     *
     *   - Event clicks open in a NEW TAB. Navigating inside the frame would
     *     trap the visitor in a ~700px box with no way back.
     *   - The Subscribe dialog is replaced by a link to the full page. A MUI
     *     modal inside a short iframe gets clipped by the frame, and no dialog
     *     can escape it.
     */
    embed?: boolean;
    /** `list` suits a narrow column; `month` is the default grid. */
    initialView?: 'month' | 'list';
}

/**
 * `PublicCalendar` — `/calendar` route (D-12).
 *
 * Mounted ABOVE the admin-framework `<Admin>` tree so this component
 * never touches the authenticated app's auth-provider chain (Pitfall 5).
 * Reads exclusively through the standalone anon-key Supabase client.
 *
 * The subscribable feed it advertises is `/api/calendar.ics` (live).
 */
export const PublicCalendar = ({
    embed = false,
    initialView = 'month',
}: PublicCalendarProps = {}) => {
    const [events, setEvents] = useState<PublicEvent[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Read once: FullCalendar only honours `initialDate` on mount, and the
    // param is rewritten on every navigation below.
    const [initialDate] = useState(() =>
        monthParamToDate(searchParams.get('month'))
    );

    // Keep the viewed month in the address (spec pages/public/calendar.md
    // §6.1). Without it the month lives only in FullCalendar's state, so any
    // reload or remount — e.g. the iframe on netscotland.org being re-laid
    // out, or a sign-in handshake reload — snaps the visitor back to today.
    // `replace` so each prev/next click does not add a history entry.
    const onDatesSet = useCallback(
        ({ view }: { view: { currentStart: Date } }) => {
            const month = dateToMonthParam(view.currentStart);
            setSearchParams(
                prev => {
                    if (prev.get('month') === month) return prev;
                    const next = new URLSearchParams(prev);
                    next.set('month', month);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    useEffect(() => {
        // Defense-in-depth: query predicates mirror the RLS policy from
        // Plan 04 `anon_read_public_events`. RLS is the truth (D-17);
        // the duplicate predicates here protect against future
        // dataProvider-bypass mistakes and make the contract obvious in
        // the source.
        supabaseAnon
            .from('events')
            .select(
                'id, title, start_date, end_date, event_type, primary_location_text'
            )
            .eq('visibility', 'public')
            .eq('status', 'scheduled')
            .eq('is_active', true)
            .order('start_date', { ascending: true })
            .then(({ data, error }) => {
                if (error) {
                    setError("Couldn't load events — try again.");
                } else {
                    setEvents((data ?? []) as PublicEvent[]);
                }
            });
    }, []);

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }
    if (!events) {
        return <Typography color="text.secondary">Loading…</Typography>;
    }

    // Empty state — UI-SPEC §"Copywriting Contract" verbatim.
    if (events.length === 0) {
        return (
            <Box textAlign="center" py={embed ? 3 : 6}>
                <Typography variant="h2" component="h2">
                    No upcoming events
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                >
                    Check back soon, or subscribe to be notified when new events
                    are scheduled.
                </Typography>
                {embed ? (
                    <FullPageLink sx={{ mt: 2 }} />
                ) : (
                    <SubscribeCalendarButton sx={{ mt: 3 }} />
                )}
            </Box>
        );
    }

    const fcEvents = events.map(e => ({
        id: e.id,
        title: e.title,
        start: e.start_date,
        // FullCalendar uses an EXCLUSIVE end date; bump by one day so a
        // same-day event displays as a single chip instead of vanishing
        // (mirrors InternalCalendar.tsx).
        end: addOneDay(e.end_date),
        allDay: true,
        backgroundColor: BRAND_BLUE,
        borderColor: BRAND_BLUE,
        textColor: '#FFFFFF',
    }));

    return (
        <Stack spacing={embed ? 1.5 : 3}>
            {embed ? (
                // The host page supplies its own heading; a second one reads as
                // a mistake. Subscribing still needs to be reachable, so the
                // full page is one click away in a new tab.
                <Box textAlign="right">
                    <FullPageLink />
                </Box>
            ) : (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Typography variant="h2" component="h2">
                        Upcoming events
                    </Typography>
                    <SubscribeCalendarButton />
                </Stack>
            )}
            <FullCalendar
                plugins={[dayGridPlugin, listPlugin]}
                initialView={
                    initialView === 'list' ? 'listMonth' : 'dayGridMonth'
                }
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,listMonth',
                }}
                buttonText={{
                    today: 'Today',
                    month: 'Month',
                    list: 'List',
                }}
                initialDate={initialDate}
                datesSet={onDatesSet}
                events={fcEvents}
                eventClick={info => {
                    // In a frame, in-place navigation traps the visitor in a
                    // ~700px box with no browser chrome to get back with.
                    if (embed) {
                        window.open(`/event/${info.event.id}`, '_blank');
                        return;
                    }
                    navigate(`/event/${info.event.id}`);
                }}
                height="auto"
                displayEventTime={false}
            />
        </Stack>
    );
};

/** "See all events" — the embed's escape hatch to the real page. */
const FullPageLink = ({ sx }: { sx?: object }) => (
    <MuiLink
        href="/calendar"
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        sx={sx}
    >
        See all events and subscribe ↗
    </MuiLink>
);

/** `YYYY-MM` → first-of-month ISO date, or undefined (→ current month). */
export function monthParamToDate(
    value: string | null | undefined
): string | undefined {
    if (!value) return undefined;
    const m = /^(\d{4})-(\d{2})$/.exec(value);
    if (!m) return undefined;
    const month = Number(m[2]);
    if (month < 1 || month > 12) return undefined;
    return `${m[1]}-${m[2]}-01`;
}

/** Local calendar month of `d` as `YYYY-MM`. */
export function dateToMonthParam(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function addOneDay(isoDate: string): string {
    if (!isoDate) return isoDate;
    const d = new Date(isoDate + 'T00:00:00Z');
    if (isNaN(d.getTime())) return isoDate;
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().split('T')[0];
}

export default PublicCalendar;
